import { EmploymentType, EnterpriseStructure, OwnershipType, isExtendedStructure } from '@brighte/validation';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { getApplicantType } from '../components/StepNavigator/helper';
import { ApplicationStep } from '../graphql';
import { getRepaymentDetails } from '../steps/FinancePurchase/utils/helpers';
import { APPLICATION_STEPS, getApplicantTypeAndTrackingIdFromUrl, toSnakeCase } from '../utils';
import { INCOME_VERIFICATION } from '../utils/constants';
import { AnalyticsEvent, CUSTOMER_APPLICATION_SOURCE } from './constants';
import {
  AnalyticsBaseEventsPayload,
  AnalyticsBusinessStepPayload,
  AnalyticsEventStepPayload,
  AnalyticsOwnershipStepPayload,
  TransformAnalyticsPayloadByStepProps,
  TransformBaseAnalyticsPayloadProps,
} from './types';

export const analytics = AnalyticsBrowser.load({
  writeKey: import.meta.env.VITE_SEGMENT_WRITEKEY,
});

export const identifyAnalyticsUser = (user: string, properties?: Record<string, unknown>) =>
  analytics.identify(user, properties);

export const trackAnalyticsEvent = (event: AnalyticsEvent, properties?: Record<string, unknown>) =>
  analytics.track(event, { event_source: CUSTOMER_APPLICATION_SOURCE, ...properties });

const eventNamesMap = {
  [APPLICATION_STEPS.INCOME_VERIFICATION]: AnalyticsEvent.FinanceApplicationIncomeVerificationSubmitted,
  [APPLICATION_STEPS.FINANCE_PURCHASE]: AnalyticsEvent.FinanceApplicationRepaymentTermsSubmitted,
  [APPLICATION_STEPS.ELIGIBILITY]: AnalyticsEvent.FinanceApplicationEligibilitySubmitted,
  [APPLICATION_STEPS.IDENTIFICATION]: AnalyticsEvent.FinanceApplicationIdentificationSubmitted,
  [APPLICATION_STEPS.HOUSEHOLD]: AnalyticsEvent.FinanceApplicationHouseholdDetailsSubmitted,
  [APPLICATION_STEPS.REPAYMENT_ACCOUNT]: AnalyticsEvent.FinanceApplicationPaymentDetailsSubmitted,
  [APPLICATION_STEPS.SUMMARY]: AnalyticsEvent.FinanceApplicationSent,
  // for sfa
  [APPLICATION_STEPS.BUSINESS_DETAILS]: AnalyticsEvent.FinanceApplicationBusinessDetailsSubmitted,
  [APPLICATION_STEPS.OWNERSHIP_DETAILS]: AnalyticsEvent.FinanceApplicationOwnershipDetailsSubmitted,
  [APPLICATION_STEPS.BENEFICIAL_OWNER]: AnalyticsEvent.FinanceApplicationBeneficialOwnerSubmitted,
};

export const getEventNameByStep = (currentPage: ApplicationStep) => eventNamesMap[currentPage];

const convertKeysToSnakeCase = (obj, prefix, index) => {
  return Object.entries(obj).reduce((result, item) => {
    const propertyKey = prefix
      ? `${prefix}_${index + 1}_${toSnakeCase(item[0])}` // formats to director_1_full_name
      : toSnakeCase(item[0]); // formats to full_name
    result[propertyKey] = item[1];
    return result;
  }, {});
};

const transformDirectors = directors => {
  return directors.reduce((res, properties, index) => {
    const { middleName, ...rest } = properties;
    const transformData = convertKeysToSnakeCase(
      {
        ...rest,
        fullName: `${rest.firstName}${middleName ? ' ' + middleName : ''} ${rest.lastName}`,
      },
      'director',
      index,
    );
    return { ...res, ...transformData };
  }, {});
};

const transformBeneficialOwners = data => {
  return data.reduce((result, item, index) => {
    const transformData = convertKeysToSnakeCase(
      {
        idType: item.identification.type.toLowerCase(),
      },
      'beneficial_owner',
      index,
    );

    return {
      ...result,
      ...transformData,
    };
  }, {});
};

const getIncomeVerification = (income): string => (income.documents.length === 0 ? 'Bank Statement' : 'Documents');

const getEmploymentDetails = employment => {
  const { PENSIONER_OPTIONS, INCOME_OPTIONS } = INCOME_VERIFICATION;
  const employmentType = employment?.type;

  if (!employmentType) return {};

  const employmentDetailsLookup = {
    [EmploymentType.EMPLOYED]: {
      employer_name: employment?.employed?.employerBusinessName,
      ...(employment?.employed?.jobTitle && {
        job_title: employment?.employed?.jobTitle,
      }),
    },
    [EmploymentType.SELF_EMPLOYED]: {
      abn_number: employment?.selfEmployed?.abn,
    },
    [EmploymentType.PENSIONER]: {
      pensioner_type: PENSIONER_OPTIONS.find(option => option.value === employment?.pensioner?.pensionType)?.label,
    },
  };

  return {
    ...employmentDetailsLookup[employmentType],
    income_type: INCOME_OPTIONS.find(item => item.value === employmentType)?.label,
  };
};

const getAdditionalIncome = income => {
  if (!income.isAdditionalIncome) return {};

  return {
    additional_income: income.additional.amount,
    additional_income_frequency: income.additional.period,
    additional_income_verification: getIncomeVerification(income.additional),
  };
};

export function transformBaseAnalyticsPayload({
  quote,
  formValues,
  user,
}: TransformBaseAnalyticsPayloadProps): Partial<AnalyticsBaseEventsPayload> | null {
  if (!quote || !formValues) return null;
  const { applicantType } = getApplicantTypeAndTrackingIdFromUrl();
  const { Finance } = formValues;
  const repayment = getRepaymentDetails(Finance?.repaymentTerm ?? '', quote);
  return {
    applicant_type: getApplicantType(applicantType),
    application_loan_repayments_term_in_months: repayment?.termInMonths,
    application_loan_repayments_value: repayment?.amount,
    email: user?.email,
    event_source: CUSTOMER_APPLICATION_SOURCE,
    loan_type: quote?.financialProduct?.id,
    product_category: quote.category?.group,
    promotion_code: quote.promotionCode ?? null,
    product_type: quote?.category?.name,
    quote_id: quote?.id,
    role_type: user?.role,
    user_id: user?.uid,
    vendor_id: quote?.vendor?.publicId,
  };
}

export const transformAnalyticsPayloadByStep = ({
  currentPage,
  formValues,
  quote,
}: TransformAnalyticsPayloadByStepProps): AnalyticsEventStepPayload | null => {
  const stepFormValues = formValues[currentPage];

  switch (currentPage) {
    case APPLICATION_STEPS.FINANCE_PURCHASE: {
      if (!quote) return null;

      const repayment = getRepaymentDetails(stepFormValues.repaymentTerm, quote);
      const { address } = stepFormValues;

      return {
        application_loan_value: quote?.financedAmount,
        application_purchase_amount: quote?.totalPurchaseAmount,
        application_total_minimum_payable: repayment?.totalPayments,
        customer_type: quote?.financialProduct.customerType,
        deposit_amount: quote?.depositAmount,
        installation_city: address?.suburb,
        installation_state: address?.state,
        installation_street_address: address ? `${address.streetNumber} ${address.streetName}` : null,
        installation_postcode: address?.postcode,
        ...(repayment?.interestRate !== undefined && {
          interest_rate: `${repayment?.interestRate}% p.a`,
        }),
        loan_category: quote?.financialProduct.type,
        product_description: quote?.productDescription,
        vendor_name: quote?.vendor.tradingName,
      };
    }

    case APPLICATION_STEPS.ELIGIBILITY: {
      if (!stepFormValues?.applicant) return {};

      return {
        unsolicited_sale_option: stepFormValues.applicant.unsolicitedSaleOptions,
      };
    }

    case APPLICATION_STEPS.IDENTIFICATION: {
      const {
        applicant: { identification },
      } = stepFormValues;
      return {
        id_type: identification.type,
      };
    }

    case APPLICATION_STEPS.INCOME_VERIFICATION: {
      const {
        applicant: { income, employment },
      } = stepFormValues;

      let incomeDetails = {};

      if (income?.primary?.documents || income?.primary?.bankStatementToken) {
        incomeDetails = {
          income_verification: getIncomeVerification(income.primary),
        };
      }

      if (income?.isAdditionalIncome) {
        incomeDetails = {
          ...incomeDetails,
          additional_income_available: income.isAdditionalIncome.toString(),
          ...getAdditionalIncome(income),
        };
      }

      return {
        ...incomeDetails,
        income_frequency: income?.primary?.period,
        net_income: income?.primary?.amount,
        ...getEmploymentDetails(employment),
      };
    }

    case APPLICATION_STEPS.HOUSEHOLD: {
      const {
        applicant: { maritalStatus, dependents, ownProperty, income, expense },
        hasJointApplicant,
      } = stepFormValues;

      return {
        household_status: maritalStatus,
        ...(dependents && {
          dependants: parseInt(dependents),
        }),
        ...(ownProperty && {
          home_owner: ownProperty,
        }),
        ...(income?.householdIncome && {
          household_income: income.householdIncome.amount,
          household_income_frequency: income.householdIncome.period,
        }),
        ...(income?.isSkipPrimaryIncome && {
          income_verification_skipped: income.isSkipPrimaryIncome,
        }),
        ...(expense?.mortgage && {
          mortgage_repayments: expense.mortgage.amount,
          mortgage_repayments_frequency: expense.mortgage.period,
        }),
        ...(expense?.rent && {
          rent_repayments: expense.rent.amount,
          rent_repayments_frequency: expense.rent.period,
        }),
        ...(expense?.otherLoans && {
          other_loan_repayments: expense.otherLoans.amount,
          other_loan_repayments_frequency: expense.otherLoans.period,
        }),
        ...(expense?.investmentProperties && {
          investment_properties_repayments: expense.investmentProperties.amount,
          investment_properties_repayments_frequency: expense.investmentProperties.period,
        }),
        ...(expense?.creditCards && {
          credit_card_limit: expense.creditCards.limit,
        }),
        ...(expense?.household && {
          household_expenses: expense.household.amount,
          household_expenses_frequency: expense.household.period,
        }),
        ...(maritalStatus === 'married' || maritalStatus === 'deFacto'
          ? {
              joint_application: hasJointApplicant,
              ...(income && {
                partner_income: income.partnerIncome.amount,
                partner_income_frequency: income.partnerIncome.period,
              }),
            }
          : {}),
      };
    }

    case APPLICATION_STEPS.BUSINESS_DETAILS: {
      const { enterprise } = stepFormValues;
      const entity_structure = enterprise.structure;

      if (isExtendedStructure(enterprise.structure)) {
        const isTrust = enterprise.structure === EnterpriseStructure.Trust;
        const hasTrustDeed =
          (Array.isArray((enterprise as Record<string, unknown>).trustDeedFiles) &&
            ((enterprise as Record<string, unknown>).trustDeedFiles as unknown[]).length > 0) ||
          (Array.isArray((enterprise as Record<string, unknown>).trustDeedDocumentIds) &&
            ((enterprise as Record<string, unknown>).trustDeedDocumentIds as unknown[]).length > 0);

        return {
          entity_structure,
          legal_business_name: enterprise.legalEntityName,
          registered_office_address: enterprise.registeredAddress?.fullAddress,
          trading_name: enterprise.tradingName,
          abn: enterprise.abn,
          business_phone: enterprise.businessPhone,
          business_email: enterprise.businessEmail,
          years_in_trading: enterprise.yearsInTrading,
          number_of_employees: enterprise.numberOfEmployees,
          industry_category: enterprise.industryCategory,
          property_status: enterprise.propertyStatus,
          billing_contact_same_as_primary: enterprise.billingContactSameAsPrimary,
          ...(isTrust && { trust_deed_uploaded: hasTrustDeed }),
        } satisfies AnalyticsBusinessStepPayload;
      }

      const isCompany = enterprise.structure === EnterpriseStructure.Company;

      const businessStep: AnalyticsBusinessStepPayload = {
        entity_structure,
        legal_business_name: enterprise.legalEntityName,
        registered_office_address: enterprise.registeredAddress.fullAddress,
        ...(isCompany && {
          principal_place_of_business: enterprise.principalBusinessAddress?.fullAddress,
        }),
        ...(!isCompany && {
          association_number: enterprise.associationNumber,
        }),
      };

      return businessStep;
    }

    case APPLICATION_STEPS.OWNERSHIP_DETAILS: {
      const { enterprise } = stepFormValues;
      const isCompany = enterprise.structure === EnterpriseStructure.Company;

      if (isExtendedStructure(enterprise.structure)) {
        return null;
      }

      let ownershipStep = {} as AnalyticsOwnershipStepPayload;

      if (isCompany) {
        if (enterprise.company.ownershipType === OwnershipType.Proprietary) {
          ownershipStep = {
            number_of_directors: enterprise.company.directors.length.toString(),
            ...transformDirectors(enterprise.company.directors),
            ...transformBeneficialOwners(enterprise.company.beneficialOwners),
          };
        }

        ownershipStep.ownership_details = enterprise.company.ownershipType;

        return ownershipStep;
      }

      if (
        ![
          EnterpriseStructure.Partnership,
          EnterpriseStructure.Trust,
          EnterpriseStructure.SoleTrader,
          EnterpriseStructure.ProprietaryLimitedCompany,
        ].includes(enterprise.structure)
      ) {
        const enterpriseDetails =
          enterprise.structure === EnterpriseStructure.IncorporatedAssociation
            ? enterprise?.incorporatedAssociation
            : enterprise?.unincorporatedAssociation;

        const { chairman, treasurer, secretary } = enterpriseDetails;
        ownershipStep = {
          chairman_id_type: chairman[0].identification.type.toLowerCase(),
          treasurer_id_type: treasurer[0].identification.type.toLowerCase(),
          secretary_id_type: secretary[0].identification.type.toLowerCase(),
        };

        return ownershipStep;
      }

      return ownershipStep;
    }

    case APPLICATION_STEPS.REPAYMENT_ACCOUNT:
      return { payment_method: 'Direct Debit' };

    case APPLICATION_STEPS.SUMMARY:
    default:
      return null;
  }
};
